'use client';

import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { CandidateJobSearchKeyword } from './CandidateJobSearchKeyword';
import { CandidateJobSearchLocation } from './CandidateJobSearchLocation';
import { CandidateJobSearchSalary } from './CandidateJobSearchSalary';

interface CandidateJobSearchFormProps {
  locale: Locale;
  onSearch: (filters: CandidateSearchFilters) => void;
  initialFilters?: CandidateSearchFilters;
}

/**
 * @description Candidate job search form shown in the main content area when the user
 * clicks "New job" in the sidebar.
 *
 * Collects keyword, location, and spoken languages. The Search button is enabled
 * as long as at least one field has a value.
 */
export const CandidateJobSearchForm = ({ locale, onSearch, initialFilters }: CandidateJobSearchFormProps) => {
  const { t } = useTranslation('common');

  const [keyword, setKeyword] = useState(initialFilters?.keyword ?? '');
  const [locations, setLocations] = useState<string[]>(initialFilters?.locations ?? []);
  const [salary, setSalary] = useState(initialFilters?.salary ?? 0);
  const hasSelection = !!(keyword.trim() || locations.length || salary);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!hasSelection) return;
    onSearch({
      ...(keyword.trim() && { keyword: keyword.trim() }),
      ...(locations.length && { locations }),
      ...(salary && { salary }),
    });
  }, [keyword, locations, salary, hasSelection, onSearch]);

  return (
    <div className="flex grow flex-col items-center justify-end px-4 pb-16 pt-8 md:pb-10">
      <div className="flex w-full max-w-2xl items-center gap-3 md:gap-4">
        <form id="candidate-job-search-form" onSubmit={handleSubmit} className="grid flex-1 gap-4">
          {/* Keyword (mobile: shares a row with the search button) */}
          <div className="flex items-center gap-3 md:block">
            <section className="flex-1 rounded-3xl border border-input/60 bg-surface px-5 py-4.5 shadow-xs">
              <span className="mb-2 block text-sm font-semibold text-ink-medium">
                {t('job.add.label.title')}
              </span>
              <CandidateJobSearchKeyword
                locale={locale}
                value={keyword}
                onChange={setKeyword}
              />
            </section>

            <Button
              type="submit"
              form="candidate-job-search-form"
              styling="outline"
              color="light"
              rounding="none"
              aria-label={t('mc.search.job')}
              title={t('mc.search.job')}
              className="!h-14 !w-14 shrink-0 !rounded-full bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary md:hidden"
            >
              <MagnifyingGlassIcon className="size-6 text-ink" />
            </Button>
          </div>

          {/* Location & Salary */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <section className="rounded-3xl border border-input/60 bg-surface px-5 py-4.5 shadow-xs">
              <span className="mb-2 block text-sm font-semibold text-ink-medium">
                {t('job.add.label.job_location')}
              </span>
              <CandidateJobSearchLocation onChange={setLocations} initialLocations={initialFilters?.locations} />
            </section>

            <section className="rounded-3xl border border-input/60 bg-surface px-5 py-4.5 shadow-xs">
              <span className="mb-2 block text-sm font-semibold text-ink-medium">
                {t('job.add.label.net_salary')}
              </span>
              <CandidateJobSearchSalary
                onChange={setSalary}
                initialSalary={initialFilters?.salary}
              />
            </section>
          </div>
        </form>

        <Button
          type="submit"
          form="candidate-job-search-form"
          styling="outline"
          color="light"
          rounding="none"
          aria-label={t('mc.search.job')}
          title={t('mc.search.job')}
          className="hidden !h-14 !w-14 shrink-0 !rounded-full bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary md:flex"
        >
          <MagnifyingGlassIcon className="size-6 text-ink" />
        </Button>
      </div>
    </div>
  );
};
